import { Show as BaseShow } from "@/lib/mockData";
import { useTranslation } from "react-i18next";
import { formatTimeDisplay } from "@/lib/utils/time";
import { getCurrencySymbol } from "@/lib/utils/currency";

interface Show extends BaseShow {
  isAvailable?: boolean;
}

interface ShowCardVariant5Props {
  show: Show;
  onBookNow: () => void;
  onInfoClick: () => void;
  bookingType?: string;
  selectedDate?: Date;
}

const getGroupSizeNumbers = (groupSize: string) => {
  const match = groupSize.match(/(\d+)-(\d+)/);
  if (match) {
    const min = parseInt(match[1]);
    const max = parseInt(match[2]);
    return { min, max, avg: Math.ceil((min + max) / 2) };
  }
  return { min: 1, max: 1, avg: 1 };
};

export function ShowCardVariant5({
  show,
  onBookNow,
  onInfoClick,
  bookingType,
  selectedDate,
}: ShowCardVariant5Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const groupNumbers = getGroupSizeNumbers(show.groupSize);

  // Check if booking is within cut-off period
  const isWithinCutOff = (() => {
    if (!selectedDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day

    const bookingDate = new Date(selectedDate);
    bookingDate.setHours(0, 0, 0, 0); // Reset to start of day

    const cutOffDays = show.cut_off_days ?? 7; // Default to 7 days if not specified
    const daysDifference = Math.floor((bookingDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    return daysDifference < cutOffDays;
  })();
  // show.price is already per-person from API
  const pricePerPerson = show.price;
  // For mobile, use "kr" instead of "SEK"
  const currencyDisplay = show.currency === "SEK" ? "kr" : getCurrencySymbol(show.currency || "SEK");
  const priceText = `${pricePerPerson} ${currencyDisplay}`;
  // Remove per person suffix on mobile - pricing info text will clarify this
  const priceTypeLabel = "";

  return (
    <div
      className={`bg-card rounded-lg border shadow-sm hover:shadow-md transition-shadow p-4 relative ${show.isAvailable === false ? "overflow-hidden" : ""}`}
    >
      {show.isAvailable === false && (
        <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 z-10 pointer-events-none" />
      )}
      <div className="space-y-3 relative">
        {/* Clickable area for details - now a button for WCAG */}
        <button
          className="w-full text-left space-y-3 -m-1 p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded hover:bg-transparent bg-transparent overflow-visible"
          onClick={onInfoClick}
          aria-label={t("booking.shows.card.viewDetails", {
            name: show.name,
            time: formatTimeDisplay(show.startTime, locale),
            price: pricePerPerson,
          })}
        >
          {/* Primary info - Time and price centered with dot separator */}
          <div className="flex items-center justify-center gap-2">
            <div className="font-semibold text-lg">
              🕐 {formatTimeDisplay(show.startTime, locale)}
            </div>
            <span className="text-muted-foreground">•</span>
            <div className="font-semibold text-lg">
              💰 {priceText}
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-border"></div>

          {/* Show information centered */}
          <div className="text-center space-y-1 min-h-0">
            <h3 className="font-semibold text-base">{show.name}</h3>
            <p className="text-sm text-muted-foreground">{show.subtitle}</p>
            {/* Remove participants and duration from mobile view for cleaner look */}
            {/* Add subtle note when within cut-off period (only if not sold out) */}
            {isWithinCutOff && show.isAvailable !== false && (
              <div className="text-xs text-amber-600 dark:text-amber-500 font-medium mt-1 visible">
                ⚠️ {t("booking.shows.card.cutOffNote", { count: show.cut_off_days ?? 7 })}
              </div>
            )}
          </div>
        </button>
      </div>
    </div>
  );
}
